import React from 'react';
import { Skeleton } from 'antd';

import Text from '@/v2/components/Typography/Text';
import Colors from '@/v2/constants/Colors';

import { Container, Body, AddonContainer } from './SelectableCard.styled';

type SelectableCardProps = {
  title?: string;
  subtitle?: string;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  disabled?: boolean;
  addonBefore?: React.ReactNode;
  variant?: 'vertical' | 'horizontal';
  size?: 'default' | 'compact';
  children?:
    | React.ReactNode
    | (({ addonBefore }: { addonBefore: React.ReactNode }) => React.ReactNode);
  ariaLabel?: string;
  ariaRole?: string;
  testId?: string;
};

export default function SelectableCard({
  title,
  subtitle,
  selected,
  onSelect,
  disabled,
  addonBefore,
  variant = 'vertical',
  size = 'default',
  children,
  ariaLabel,
  ariaRole,
  testId,
}: SelectableCardProps) {
  const isChildrenAFunction = typeof children === 'function';
  const isSelectableControl = ariaRole == 'checkbox' || ariaRole == 'radio';
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key != 'Enter' && event.key != ' ') {
      return;
    }

    event.preventDefault();
    if (!disabled) {
      onSelect?.(!selected);
    }
  };

  return (
    <Container
      onClick={() => !disabled && onSelect?.(!selected)}
      selected={selected}
      disabled={disabled}
      variant={variant}
      size={size}
      role={ariaRole}
      aria-label={ariaLabel}
      aria-checked={isSelectableControl ? selected : undefined}
      aria-disabled={disabled || undefined}
      tabIndex={isSelectableControl && !disabled ? 0 : undefined}
      onKeyDown={isSelectableControl ? onKeyDown : undefined}
      data-testid={testId}
    >
      {addonBefore && !isChildrenAFunction ? (
        <AddonContainer aria-hidden="true">{addonBefore}</AddonContainer>
      ) : null}
      <Body>
        {title && (
          <Text variant="cardTitle" color="primary" fontWeight={700}>
            {title}
          </Text>
        )}
        {subtitle && (
          <Text
            variant="grey-14-demibold"
            color={Colors.ACCENT_VARIANTS[100]}
            fontWeight={700}
          >
            {subtitle}
          </Text>
        )}
        {isChildrenAFunction ? children({ addonBefore }) : children}
      </Body>
    </Container>
  );
}

SelectableCard.Skeleton = function SelectableCardSkeleton() {
  return (
    <Container>
      <Skeleton.Avatar size="small" active />
      <Skeleton.Input size="small" active />
    </Container>
  );
};
